import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { EMPTY, iif, of } from 'rxjs';
import { map, mergeMap, withLatestFrom, catchError } from 'rxjs/operators';

/* notification service */
import { NotificationService } from 'src/app/features/shared/services/notification-service/notification.service';

/* jsonata object class */
import { JSONAtaObject } from 'src/app/features/shared/helper-classes';

/* lodash lib */
import * as lodash from 'lodash';

/* DataStorage interface for ngrx */
import { DataStorage } from 'src/app/features/data-stores/data-storage.interface';

/* navigate action */
import { Navigate } from 'src/app/features/data-stores/router-data-store/state/router-data-store.actions';

/* Workflow Management API Service */
import { WorkflowManagementControllerService } from 'src/app/features/shared/sdk/services/workflow-management-controller.service';

/* provider-data-store actions */
import * as ProviderDataStoreActions from 'src/app/features/data-stores/provider-data-store/state/provider-data-store.actions';

/* api services */
import { ProviderControllerService } from 'src/app/features/shared/sdk/services';

@Injectable()
export class ProviderDataStoreEffects {
  constructor(
    private actions$: Actions,
    private store: Store<DataStorage>,
    private notificationService: NotificationService,
    private workflowManagementApi: WorkflowManagementControllerService,
    private providerApiService: ProviderControllerService,
  ) { 
    // prettier-ignore
    // #region customCode: data-store-effects__constructor__start
    
    // #endregion customCode: data-store-effects__constructor__start

    // prettier-ignore
    // #region customCode: data-store-effects__constructor__end
    
    // #endregion customCode: data-store-effects__constructor__end
  }

  getProviders$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProviderDataStoreActions.GetProviders),
      mergeMap((action) => {
        return this.providerApiService.find().pipe(
          mergeMap((providers) => {
            const payload: any = {
              providers: providers,
            };
            return [
              ProviderDataStoreActions.GetProvidersSuccessful({ payload }),
            ];
          }),
          catchError((error) => {
            this.notificationService.createNotification(
              'error',
              'Failure',
              'There was an error while fetching data.',
            );
            return [ProviderDataStoreActions.GetProvidersFailure()];
          }),
        );
      }),
    ),
  );

  getProviderById$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProviderDataStoreActions.GetProviderById),
      mergeMap((action) => {
        return this.providerApiService
          .findById({
            id: action.payload.id,
          })
          .pipe(
            mergeMap((provider) => {
              const payload: any = {
                provider: provider,
              };
              return [
                ProviderDataStoreActions.GetProviderByIdSuccessful({ payload }),
              ];
            }),
            catchError((error) => {
              this.notificationService.createNotification(
                'error',
                'Failure',
                'There was an error while fetching data.',
              );
              return [ProviderDataStoreActions.GetProviderByIdFailure()];
            }),
          );
      }),
    ),
  );

  createProvider$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProviderDataStoreActions.CreateProvider),
      mergeMap((action) => {
        const params = {
          body: action.payload.provider,
        };
        return this.providerApiService.create(<any>params).pipe(
          mergeMap((provider) => {
            this.notificationService.createNotification(
              'success',
              'Successful',
              'Your data has been saved successfully.',
            );
            const payload: any = {
              provider: provider,
            };
            return [
              ProviderDataStoreActions.CreateProviderSuccessful({ payload }),
            ];
          }),
          catchError((error) => {
            this.notificationService.createNotification(
              'error',
              'Failure',
              'There was an error while saving the data.',
            );
            return [ProviderDataStoreActions.CreateProviderFailure()];
          }),
        );
      }),
    ),
  );

  updateProvider$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProviderDataStoreActions.UpdateProvider),
      mergeMap((action) => {
        const params = {
          id: action.payload.id,
          body: action.payload.data,
        };
        return this.providerApiService.updateById(<any>params).pipe(
          mergeMap(() => {
            this.notificationService.createNotification(
              'success',
              'Successful',
              'Your data has been updated successfully.',
            );
            return [ProviderDataStoreActions.GetProviders()];
          }),
          catchError((error) => {
            this.notificationService.createNotification(
              'error',
              'Failure',
              'There was an error while updating the data.',
            );
            return [ProviderDataStoreActions.UpdateProviderFailure()];
          }),
        );
      }),
    ),
  );

  deleteProvider$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProviderDataStoreActions.DeleteProvider),
      mergeMap((action) => {
        const params = { id: action.payload.id };
        return this.providerApiService.deleteById(params).pipe(
          mergeMap(() => {
            this.notificationService.createNotification(
              'success',
              'Success',
              'Your data has been deleted successfully.',
            );
            const payload: any = {
              id: action.payload.id,
            };
            return [
              ProviderDataStoreActions.DeleteProviderSuccessful({ payload }),
            ];
          }),
          catchError((error) => {
            this.notificationService.createNotification(
              'error',
              'Failure',
              'There was an error while deleting the data.',
            );
            return [ProviderDataStoreActions.DeleteProviderFailure()];
          }),
        );
      }),
    ),
  );
}
